import type { ActorKind } from '~/types/actor.types';

/**
 * The workflow-service audit contract exposes its actor as a *string* (an email,
 * the `WORKFLOW-SERVICE` sentinel, a service token, `UNKNOWN`, or `null`) rather
 * than the rich `ActorRef` object the integrations API ships. This module is the
 * bridge: it parses that string into the fields the shared `ActorLabel` renderer
 * needs, so both services funnel through one renderer. If workflow-service later
 * adopts the object envelope, delete this and pass the object straight through.
 */

/** The `WORKFLOW-SERVICE` sentinel a Temporal worker records for autonomous actions. */
const WORKFLOW_SERVICE_SENTINEL = 'WORKFLOW-SERVICE';
/** Backfill marker the backend writes for rows that predate actor tracking. */
const UNKNOWN_SENTINEL = 'UNKNOWN';
/** `"alice@lily.ai (on behalf of integration-x)"` — an integration acting for a person. */
const ON_BEHALF_OF = /^(.*?)\s*\(on behalf of\s+(.+?)\)\s*$/i;

/** Parsed shape of a workflow-service actor string, i18n-free so it stays pure/testable. */
export interface ParsedActorString {
  kind: ActorKind;
  /** The principal token (email or service id), with any "on behalf of" clause stripped. */
  raw: string;
  email: string | null;
  /** False only for the `UNKNOWN` backfill marker — a short-id/muted state, not an error. */
  resolved: boolean;
  /** The delegate a person acted through, from `"X (on behalf of Y)"`; null otherwise. */
  onBehalfOf: string | null;
}

/**
 * Parses a workflow-service actor string into `{ kind, email, resolved, onBehalfOf }`.
 * Returns `null` for an absent actor ("no actor for this role") — distinct from
 * `UNKNOWN`, which is a present-but-unresolvable actor. Pure: label text is the
 * caller's (see `ActorStringLabel`) so this carries no i18n.
 */
export function parseActorString(value: string | null | undefined): ParsedActorString | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const behalfMatch = ON_BEHALF_OF.exec(trimmed);
  const principal = (behalfMatch?.[1] ?? trimmed).trim();
  const onBehalfOf = behalfMatch?.[2]?.trim() ?? null;
  if (!principal) return null;

  if (principal.toUpperCase() === WORKFLOW_SERVICE_SENTINEL) {
    return { kind: 'SYSTEM', raw: principal, email: null, resolved: true, onBehalfOf };
  }
  if (principal.toUpperCase() === UNKNOWN_SENTINEL) {
    return { kind: 'UNKNOWN', raw: principal, email: null, resolved: false, onBehalfOf };
  }
  if (principal.includes('@')) {
    return { kind: 'USER', raw: principal, email: principal, resolved: true, onBehalfOf };
  }
  // A non-email, non-sentinel token is a service principal (an azp client id).
  return { kind: 'SERVICE', raw: principal, email: null, resolved: true, onBehalfOf };
}
